import { Button, Flex } from "@daangn/carotene";
import { IconHorizline3VerticalLine } from "@daangn/react-monochrome-icon";
import { BrowserRegionCookieStorage } from "@karrotmarket-com/persistent-storage/client";
import clsx from "clsx";
import { motion } from "framer-motion";

import { GL_APPSFLYER_LINK } from "~/constants";
import { CommonSearchParamKey, buildRegionParamValue } from "~/core";
import { useDisclosure } from "~/hooks/useDisclosure";
import { Position, useMoveToApp } from "~/hooks/useMoveToApp";
import { Link, useLocale, useRootLoaderData } from "~/remix-lib";
import type { RootLoaderData } from "~/root";
import { ExternalLink } from "~/services/ExternalLink";
import KarrotLogo from "~/services/KarrotLogo";
import PageMaxWidthWrapper from "~/services/PageMaxWidthWrapper";
import GlobalQrModal from "~/services/QrModal";
import { parseNavLinkWithSubLinksList } from "~/services/gnb/common/GNBParse";
import { SearchButton } from "~/services/gnb/common/SearchButton";
import {
  SearchPopover,
  SearchPopoverAnchor,
  SearchPopoverContent,
  SearchPopoverTrigger,
} from "~/services/gnb/common/SearchPopover";
import SelectRegionButton from "~/services/gnb/common/SelectRegionButton";
import { useGNBBorder } from "~/services/gnb/common/useGNBBorder";
import { useSearchButton } from "~/services/gnb/common/useSearchButton";
import { SearchBar } from "~/services/gnb/global/SearchBar";
import { globalServiceHomeUrl } from "~/site-urls";
import { GtmVariableName } from "~/utils/GtmVariable";
import { makeKeywordLinks } from "~/utils/makeKeywordLinks";
import LocationSelectModal from "../common/LocationSelectModal";
import * as css from "./GlobalNavigationBar.css";
import HamburgerMenu from "./GlobalNavigationBar__HamburgerMenu";
import { SearchForm } from "./SearchForm";

const regionBrowserStorage = BrowserRegionCookieStorage.of();

export type GlobalNavigationBarProps = {
  data: RootLoaderData["__gnb"]["data"];
  query: string | null;
  isDetailPage?: boolean;
};

const GlobalNavigationBar: React.FC<GlobalNavigationBarProps> = ({
  isDetailPage = false,
  ...props
}) => {
  const rootData = useRootLoaderData();

  const { t } = useLocale();

  const showBorder = useGNBBorder();
  const showSearchButton = useSearchButton(false);

  const {
    isOpen: isHamburgerMenuOpen,
    setOpenState: setHamburgerMenuOpenState,
    onClose: closeHamburgerMenu,
  } = useDisclosure();

  const { moveToApp, setQrModalStatus, isQrModalOpen } = useMoveToApp();

  const keywordLinks = makeKeywordLinks({
    pathname: `/${rootData.karrotLocalCountryCode}${globalServiceHomeUrl.BUY_SELL}`,
    region: rootData.region,
    keywordRecord: props.data.keywords,
  });

  const clickDownloadButton = () => {
    moveToApp({
      position: Position.GNB,
      appScheme: GL_APPSFLYER_LINK.Mobile.Feed,
    });
  };

  const parsedNavLinkWithSubLinksList = parseNavLinkWithSubLinksList(
    props.data.GNBSubLink?.NavLinkWithSubLinks,
  );

  return (
    <>
      <div className={css.container} data-border={showBorder}>
        <PageMaxWidthWrapper>
          <div className={css.top}>
            <Link
              data-gtm={GtmVariableName.GnbLogo}
              href={`/${rootData.karrotLocalCountryCode}/?${
                CommonSearchParamKey.In
              }=${buildRegionParamValue({ region: rootData.region })}`}
              className={css.logo}
              aria-label="Go to karrotmarket.com home"
            >
              <KarrotLogo location="gnb" />
            </Link>
            <nav className={css.topRightNav} aria-label="Navigation menus">
              <ul className={css.topRightNavList}>
                {parsedNavLinkWithSubLinksList.map((linkWithSubLinks) => (
                  <li key={linkWithSubLinks.Link.NavLinkLabel}>
                    {linkWithSubLinks.Link.NavLinkExternal ? (
                      <ExternalLink
                        data-gtm={GtmVariableName.GnbMenu}
                        className={css.navLink}
                        href={linkWithSubLinks.Link.NavLinkLink}
                      >
                        {linkWithSubLinks.Link.NavLinkLabel}
                      </ExternalLink>
                    ) : (
                      <Link
                        data-gtm={GtmVariableName.GnbMenu}
                        className={css.navLink}
                        href={linkWithSubLinks.Link.NavLinkLink}
                      >
                        {linkWithSubLinks.Link.NavLinkLabel}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
            <Flex
              alignItems="center"
              gap={4}
              display={{ base: "none", medium: "flex" }}
            >
              <SearchPopover>
                <SearchPopoverTrigger asChild>
                  <SearchButton
                    visible={isDetailPage || showSearchButton}
                    label="Search"
                    className={css.searchPopoverTriggerSelector}
                  />
                </SearchPopoverTrigger>
                <SearchPopoverAnchor className={css.searchPopoverAnchor} />
                <SearchPopoverContent
                  className={css.searchPopoverContent}
                  hideWhenDetached
                >
                  <SearchBar
                    region={rootData.region}
                    keywordType={props.data.keywords.type}
                    keywordComponentType="chip"
                    keywords={keywordLinks}
                    size="medium"
                    searchForm={
                      <SearchForm
                        karrotLocalCountryCode={rootData.karrotLocalCountryCode}
                        region={rootData.region}
                        query={props.query}
                        searchAutoFocus={true}
                      />
                    }
                    onSaveRegionToStorage={regionBrowserStorage.update}
                  />
                </SearchPopoverContent>
              </SearchPopover>
              <Button
                size="medium"
                variant="brandWeak"
                data-gtm={GtmVariableName.GnbDownload}
                onClick={clickDownloadButton}
              >
                {t("GlobalNavigationBar.app_download_button_label")}
              </Button>
            </Flex>
            <div className={clsx(css.topRightActionButton, css.mobileOnly)}>
              <Flex
                gap={{
                  base: 2,
                  small: 3,
                }}
                alignItems="center"
              >
                <motion.div
                  initial={false}
                  animate={{
                    x: isDetailPage || showSearchButton ? 0 : 44,
                  }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                >
                  <LocationSelectModal
                    trigger={
                      <SelectRegionButton
                        size="small"
                        currentRegionName={rootData.region.name}
                      />
                    }
                    onSaveRegionToStorage={regionBrowserStorage.update}
                  />
                </motion.div>
                <SearchPopover>
                  <SearchPopoverTrigger asChild>
                    <SearchButton
                      visible={isDetailPage || showSearchButton}
                      label="Search"
                      className={css.searchPopoverTriggerSelector}
                    />
                  </SearchPopoverTrigger>
                  <SearchPopoverAnchor className={css.searchPopoverAnchor} />
                  <SearchPopoverContent
                    className={css.searchPopoverContent}
                    hideWhenDetached
                  >
                    <SearchBar
                      region={rootData.region}
                      keywordType={props.data.keywords.type}
                      keywords={keywordLinks}
                      searchForm={
                        <SearchForm
                          karrotLocalCountryCode={
                            rootData.karrotLocalCountryCode
                          }
                          region={rootData.region}
                          query={props.query}
                          searchAutoFocus={true}
                        />
                      }
                      onSaveRegionToStorage={regionBrowserStorage.update}
                    />
                  </SearchPopoverContent>
                </SearchPopover>
              </Flex>
              <div className={css.topRightHamburgerContainer}>
                <HamburgerMenu.Root
                  open={isHamburgerMenuOpen}
                  onOpenChange={setHamburgerMenuOpenState}
                >
                  <HamburgerMenu.Trigger className={css.hamburgerButton}>
                    <div
                      data-gtm={GtmVariableName.GnbHamburger}
                      className={css.hamburgerButtonIcon}
                    >
                      <IconHorizline3VerticalLine size={24} />
                    </div>
                  </HamburgerMenu.Trigger>
                  <HamburgerMenu.Content
                    navLinks={parsedNavLinkWithSubLinksList}
                    appDownloadButtonLabel={t(
                      "GlobalNavigationBar.app_download_button_label",
                    )}
                    appDownloadCaptionLabel={t(
                      "GlobalNavigationBar.app_download_caption_label",
                    )}
                    moreSubLinkButtonLabel={t(
                      "GlobalNavigationBar.see_more_button_label",
                    )}
                    onClickAppDownloadButton={clickDownloadButton}
                    onMenuLinkClick={closeHamburgerMenu}
                  />
                </HamburgerMenu.Root>
              </div>
            </div>
          </div>
        </PageMaxWidthWrapper>
      </div>
      <GlobalQrModal
        open={isQrModalOpen}
        onOpenChange={setQrModalStatus}
        qrLink={GL_APPSFLYER_LINK.Web.Feed}
      />
    </>
  );
};

export default GlobalNavigationBar;
